"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  PropsWithChildren,
  useEffect,
  useMemo,
} from "react";

import { useRouter } from "next/navigation";
import ShiprocketMiniCart from "../components/ShiprocketMiniCart";
import MiniCart from "../components/MiniCart";
import { decryptGatewayValue } from "../actions/decryptGateway";

type MiniCartContextType = {
  isOpen: boolean;
  cart: any;
  settings?: any;
  open: () => void;
  close: () => void;
  refreshCartTrigger: number;
  triggerCartRefresh: () => void;
  itemCount: number; // ✅ NEW
  activeGateway: any;
  paymentSettings: {
    id: number;
    checkoutPartner: string;
    apiKey: string;
    apiSecret: string;
    secretKey?: string;
    paymentUrl?: string;
    validationUrl?: string;
    redirectUrl?: string;
    displayName?: string;
    environment?: string;
    priority?: number;
    isActive?: boolean;
  };
  decryptedGateway?: {
    id: number;
    checkoutPartner: string;
    apiKey: string;
    apiSecret: string;
    secretKey?: string;
    paymentUrl?: string;
    validationUrl?: string;
    redirectUrl?: string;
    displayName?: string;
    environment?: string;
    priority?: number;
    isActive?: boolean;
  };
  PAYMENT_GATEWAY?: string;
};

export interface PaymentSetting {
  id: number;
  checkoutPartner: string;
  apiKey: string;
  apiSecret: string;
  secretKey?: string;
  paymentUrl?: string;
  validationUrl?: string;
  redirectUrl?: string;
  displayName?: string;
  environment?: string;
  priority?: number;
  isActive?: boolean;
}

const MiniCartContext = createContext<MiniCartContextType | undefined>(
  undefined
);

const ENV_GATEWAY = (
  process.env.NEXT_PUBLIC_PAYMENT_GATEWAY || ""
).toLowerCase();

export function MiniCartProvider({
  children,
  cart,
  settings,
}: PropsWithChildren<{ cart: any; settings: any }>) {
  const [isOpen, setIsOpen] = useState(false);
  const [refreshCartTrigger, setRefreshCartTrigger] = useState(0);
  const [decryptedGateway, setDecryptedGateway] = useState<any>(null);
  const router = useRouter();
  // ⭐ 2. Get backend gateway
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  // const paymentSettings = settings?.paymentSettings ?? [];
  const paymentSettings = useMemo(
    () => settings?.paymentSettings ?? [],
    [settings]
  );

  const activeGateway = paymentSettings?.find((p: any) => p.isActive) || null;

  const PAYMENT_GATEWAY: string = useMemo(
    () =>
      (activeGateway?.checkoutPartner?.toLowerCase() ||
        ENV_GATEWAY ||
        "razorpay") as string,
    [activeGateway]
  );
  // 🔄 Refresh trigger
  const triggerCartRefresh = () => setRefreshCartTrigger((prev) => prev + 1);

  // 🧮 Calculate item count safely
  const itemCount = cart?.attributes?.lineItems?.data?.length
    ? cart.attributes.lineItems.data.length
    : 0;

  useEffect(() => {
    async function processDecryption() {
      if (!activeGateway) return;

      try {
        const decryptedKey = await decryptGatewayValue(
          activeGateway.apiKey || ""
        );
        const decryptedSecret = await decryptGatewayValue(
          activeGateway.apiSecret || ""
        );
        const decryptedSecretKey = await decryptGatewayValue(
          activeGateway.secretKey || ""
        );
// console.log("decryptedSecretKey ",decryptedSecretKey );

        setDecryptedGateway({
          ...activeGateway,
          apiKey: decryptedKey,
          apiSecret: decryptedSecret,
          secretKey: decryptedSecretKey,
        });

        // console.log("DECRYPTED KEY:", decryptedKey);
        // console.log("DECRYPTED SECRET:", decryptedSecret);
        // console.log("DECRYPTED SECRET KEY:", decryptedSecretKey);
      } catch (error) {
        console.error("Failed to decrypt gateway:", error);
      }
    }

    processDecryption();
  }, [activeGateway]);

  const RenderCart = useMemo(() => {
    switch (PAYMENT_GATEWAY) {
      case "shiprocket":
        return ShiprocketMiniCart;
      // case "phonepe":
      //   return PhonepeMiniCart;
      case "razorpay":
      default:
        return MiniCart;
    }
  }, [PAYMENT_GATEWAY]);

  return (
    <MiniCartContext.Provider
      value={{
        isOpen,
        cart,
        open,
        close,
        refreshCartTrigger,
        triggerCartRefresh,
        itemCount, // ✅ Added here
        activeGateway,
        paymentSettings,
        decryptedGateway,
        PAYMENT_GATEWAY,
      }}
    >
      {children}
      {isOpen && <RenderCart onClose={close} />}
    </MiniCartContext.Provider>
  );
}

export function useMiniCart(): MiniCartContextType {
  const ctx = useContext(MiniCartContext);
  if (!ctx)
    throw new Error("useMiniCart() must be used within a <MiniCartProvider>.");
  return ctx;
}
